// ============================================================================
// src/lib/dailyReportOperationApi.ts — online daily-report create operations
// ============================================================================
// B3 batch (c), migration 234 (provisional). Every ONLINE create of a broiler,
// pig, layer or egg daily report (WebformHub multi-group / layer / egg, the
// Admin Add Report modal) is ONE operation: an id minted once per user intent
// and kept until the server confirms it. Sending the same operation again — a
// double tap, a lost response, a Retry — makes the server REPLAY the original
// outcome instead of inserting again. Any change to the form is a new intent
// and a new operation.
//
// The pieces:
//   * dailyIntentKey / retainOrMintOperation are PURE. They decide whether a
//     submit is a retry of the operation this surface already holds (same kind
//     and the same answers -> the SAME ids, csids and submitted_at) or a new
//     one. ids are what the server's receipt is keyed by, so reusing them on a
//     retry is the whole mechanism.
//   * photoMeta: a layer group's photos are uploaded once under the
//     operation's stable csid. The resulting metadata is kept on the operation
//     so a retry neither re-uploads (the bucket refuses a second write with
//     409) nor sends different metadata (which the server would rightly refuse
//     as different work).
//   * submitDailyReportOperation / retryDailyReportAnchors are the transport.
//     They return the RAW {data, error} envelope, because callers format a
//     failure with friendlyDailyDbError, which reads `code` and the
//     *_active_daily_identity_uq index name off it.
// ============================================================================

import {newClientSubmissionId} from './clientSubmissionId.js';
import type {SupabaseClient} from './supabaseTypes.js';

export type DailyOperationKind = 'poultry_dailys' | 'pig_dailys' | 'layer_dailys' | 'egg_dailys';
export type DailyReportRow = Record<string, unknown>;
export type DailyAnchorIntent = {row_index: number; housing_id: string};
export type DailyAnchorOutcome = {
  row_index: number;
  record_id: string;
  housing_id: string;
  status: 'applied' | 'failed' | 'superseded';
  last_error: string | null;
  attempts: number;
};

export type RetainedDailyOperation = {
  kind: DailyOperationKind;
  operationId: string;
  intentKey: string;
  // The identity the server receipt is keyed by, per row, in submitted order.
  ids: string[];
  csids: (string | null)[];
  submittedAt: string;
  // Photo metadata from a successful upload under this operation, if any.
  photoMeta: unknown[] | null;
};

// The keys an operation owns rather than the user. Excluded from the intent so
// a retry built from the same answers matches the operation it repeats.
const OPERATION_OWNED = new Set(['id', 'client_submission_id', 'submitted_at', 'photos']);

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>)
        .sort()
        .map((k) => [k, stable((value as Record<string, unknown>)[k])]),
    );
  }
  return value;
}

// A File carries no stable content handle short of reading it; name, size,
// type and lastModified identify "the same chosen photo" for retry purposes.
export function photoFingerprint(photos: readonly unknown[] | null | undefined): string[] {
  return (photos || []).map((p) => {
    const f = p as {name?: unknown; size?: unknown; type?: unknown; lastModified?: unknown};
    return [f.name, f.size, f.type, f.lastModified].map((x) => String(x ?? '')).join('|');
  });
}

export function dailyIntentKey(
  kind: DailyOperationKind,
  rows: readonly DailyReportRow[],
  photos?: readonly unknown[] | null,
): string {
  const answers = rows.map((r) => Object.fromEntries(Object.entries(r).filter(([k]) => !OPERATION_OWNED.has(k))));
  return JSON.stringify(stable({kind, answers, photos: photoFingerprint(photos)}));
}

function mintRowId(): string {
  return String(Date.now()) + Math.random().toString(36).slice(2, 8);
}

/**
 * The operation this submit belongs to. The SAME operation (ids, csids,
 * submitted_at, photo metadata) when the answers are unchanged since the last
 * unconfirmed attempt; otherwise a freshly minted one.
 */
export function retainOrMintOperation(
  previous: RetainedDailyOperation | null | undefined,
  kind: DailyOperationKind,
  rows: readonly DailyReportRow[],
  photos?: readonly unknown[] | null,
  now: () => string = () => new Date().toISOString(),
): RetainedDailyOperation {
  if (!rows.length) throw new Error('retainOrMintOperation: at least one report row is required');
  const intentKey = dailyIntentKey(kind, rows, photos);
  if (previous && previous.kind === kind && previous.intentKey === intentKey && previous.ids.length === rows.length) {
    return previous;
  }
  return {
    kind,
    operationId: newClientSubmissionId(),
    intentKey,
    ids: rows.map(() => mintRowId()),
    csids: rows.map(() => (kind === 'egg_dailys' ? null : newClientSubmissionId())),
    submittedAt: now(),
    photoMeta: null,
  };
}

/** The caller's answers with the operation's identity stamped on, row for row. */
export function operationRows(op: RetainedDailyOperation, rows: readonly DailyReportRow[]): DailyReportRow[] {
  if (rows.length !== op.ids.length) throw new Error('operationRows: rows do not match the operation');
  return rows.map((r, i) => {
    const out: DailyReportRow = {...r, id: op.ids[i], submitted_at: op.submittedAt};
    if (op.kind === 'egg_dailys') delete out.client_submission_id;
    else out.client_submission_id = op.csids[i];
    return out;
  });
}

// FNV-1a, for a compact stable suffix. Not security: the server compares the
// full canonical request, so a suffix collision can only cause a refusal.
function shortHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/**
 * The server operation id for the rows actually sent. A duplicate decision can
 * turn some groups into edits of existing reports, so the CREATE may be a
 * subset of the groups the operation was minted over. The same subset always
 * gives the same id (so a retry of that decision replays); a different subset
 * is a different operation.
 */
export function operationIdFor(op: RetainedDailyOperation, sentIds: readonly string[]): string {
  if (sentIds.length === op.ids.length && sentIds.every((id, i) => id === op.ids[i])) return op.operationId;
  const indices = sentIds.map((id) => {
    const i = op.ids.indexOf(id);
    if (i < 0) throw new Error('operationIdFor: a sent row is not part of this operation');
    return i;
  });
  return op.operationId + ':' + shortHash(indices.join('.')) + '.' + indices.length;
}

/**
 * A duplicate pre-check hit that is THIS operation's own row (a retry after a
 * lost response whose insert did commit) is not a duplicate: the server will
 * replay it. Only rows owned by some other submission are real duplicates.
 */
export function isOwnOperationRow(op: RetainedDailyOperation | null | undefined, existingId: unknown): boolean {
  return !!op && typeof existingId === 'string' && op.ids.includes(existingId);
}

export type DailyOperationResult = {
  ok?: boolean;
  replayed?: boolean;
  operation_id?: string;
  rows?: DailyReportRow[];
  anchors?: DailyAnchorOutcome[];
};

export async function submitDailyReportOperation(
  sb: SupabaseClient,
  kind: DailyOperationKind,
  operationId: string,
  stampedRows: readonly DailyReportRow[],
  actorProfileId: string,
  anchors?: readonly DailyAnchorIntent[] | null,
): Promise<{data: DailyOperationResult | null; error: unknown}> {
  if (!sb) throw new Error('submitDailyReportOperation: sb required');
  const {data, error} = await sb.rpc('submit_daily_report_operation', {
    p_form_kind: kind,
    p_operation_id: operationId,
    p_rows: stampedRows,
    p_actor_profile_id: actorProfileId,
    p_anchors: anchors && anchors.length ? anchors : null,
  });
  return {data: (data ?? null) as DailyOperationResult | null, error: error ?? null};
}

export async function retryDailyReportAnchors(
  sb: SupabaseClient,
  operationId: string,
): Promise<{data: {anchors?: DailyAnchorOutcome[]} | null; error: unknown}> {
  if (!sb) throw new Error('retryDailyReportAnchors: sb required');
  const {data, error} = await sb.rpc('retry_daily_report_anchors', {p_operation_id: operationId});
  return {data: (data ?? null) as {anchors?: DailyAnchorOutcome[]} | null, error: error ?? null};
}

/** WCF01: the server refused because this id belongs to different saved work. Never auto-retried. */
export function isOperationConflict(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as {code?: unknown}).code === 'WCF01';
}
